"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
const testimonials = [{
  author: "Eleanor M.",
  quote: "The sourdough loaf has a crust that reminds me of my grandmother's kitchen in France. Truly artisan quality that is hard to find these days.",
  position: "Local Food Critic",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/avatar_alex.png",
  initials: "EM"
}, {
  author: "Julian R.",
  quote: "Their croissants are a morning ritual for me. Flaky, buttery, and always fresh. The online ordering is seamless for my busy commute.",
  position: "Daily Regular",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/avatar_jamie.png",
  initials: "JR"
}, {
  author: "Sarah W.",
  quote: "I ordered a custom cake for my daughter's birthday and it was a work of art. Not just beautiful, but the flavor was incredibly rich and nostalgic.",
  position: "Verified Customer",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/avatar_sam.png",
  initials: "SW"
}, {
  author: "Marcus D.",
  quote: "The attention to detail in their pastries is unmatched. You can taste the premium ingredients in every single bite. A neighborhood gem.",
  position: "Pastry Enthusiast",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/avatar_taylor.png",
  initials: "MD"
}, {
  author: "Clara H.",
  quote: "CRUST has brought back the joy of real bread to our community. The golden crusts and delicate fillings are exactly what they promise.",
  position: "Verified Customer",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/avatar_jordan.png",
  initials: "CH"
}];
export default function WhatOurCommunitySays() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="227247" id="what-our-neighbors-say" className="bg-secondary py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`flex flex-col items-center text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
            What Our Neighbors Say
          </h2>
          <p className="font-default text-muted-foreground text-lg md:text-xl max-w-2xl">
            Discover why our community trusts CRUST for their daily bread and special celebrations. Real stories from our local bread lovers.
          </p>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8 space-y-6 md:space-y-8">
          {testimonials.map((testimonial, i) => <div key={i} data-index={i} className={`break-inside-avoid transition-all duration-700 ease-out delay-${i * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Card className="bg-card rounded-lg border-none shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col">
                <CardHeader className="pb-4 pt-6 px-6 md:px-8 flex flex-row items-center justify-between gap-4 border-b border-border/10">
                  <div className="flex items-center gap-4">
                    <SafeHydrate><Avatar className="size-12 rounded-full border border-primary/10">
                      <AvatarImage src={testimonial.avatar} alt={testimonial.author} className="object-cover" />
                      <AvatarFallback className="font-default bg-muted text-primary rounded-full">
                        {testimonial.initials}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="flex flex-col">
                      <span className="font-default font-semibold text-foreground text-base tracking-tight">
                        {testimonial.author}
                      </span>
                      <span className="font-default text-xs text-muted-foreground uppercase tracking-wider">
                        {testimonial.position}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 pb-8 px-6 md:px-8 flex-grow flex flex-col justify-between gap-6">
                  <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/90 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-2 mt-auto pt-4">
                    <Badge variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full border-transparent uppercase tracking-widest text-[10px] px-3 py-1 flex items-center gap-1.5">
                      <CircleCheck className="size-3" />
                      Verified Neighbor
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}